import { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import UIkit from "uikit";
import { AppContexts } from "../../../providers";

const HolidaySchedule = () => {
  const { set, settings, loading, active } = useContext(AppContexts.SettingsContext);

  // --- Federal holidays JSON (example: 2026) ---
  const federalHolidays = useMemo(
    () => [
      { date: "2026-01-01", label: "New Year’s Day" },
      { date: "2026-01-19", label: "Martin Luther King Jr. Day" },
      { date: "2026-02-16", label: "Presidents’ Day" },
      { date: "2026-05-25", label: "Memorial Day" },
      { date: "2026-06-19", label: "Juneteenth" },
      { date: "2026-07-03", label: "Independence Day (Observed)" },
      { date: "2026-09-07", label: "Labor Day" },
      { date: "2026-10-12", label: "Columbus Day" },
      { date: "2026-11-11", label: "Veterans Day" },
      { date: "2026-11-26", label: "Thanksgiving Day" },
      { date: "2026-12-25", label: "Christmas Day" },
    ],
    [],
  );

  const formatHolidayDate = (iso) => moment(iso, "YYYY-MM-DD").format("MM-DD-YY ddd");
  
  // --- Company overrides map: { "2026-07-03": { working: true, note: "" }, ... } ---
  // Default behavior: Holidays are OFF (closed). Override means "Working = true".
  const [overrides, setOverrides] = useState({});
  const [year, setYear] = useState("2026"); // simple selector (optional)

  // Load existing overrides from settings (your API can hydrate this)
  useEffect(() => {
    // Suggested place in settings:
    // settings.company_holiday_overrides = { "2026-01-01": { working: true }, ... }
    setOverrides(settings?.company_holiday_overrides || {});
  }, [settings]);

  // Filter by year (if you later load multiple years)
  const holidaysForYear = useMemo(() => {
    return federalHolidays
      .filter((h) => String(h.date).startsWith(`${year}-`))
      .map((h) => ({
        ...h,
        dow: moment(h.date, "YYYY-MM-DD").format("ddd"),
      }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [federalHolidays, year]);

  const isWorking = (isoDate) => !!overrides?.[isoDate]?.working;

  const toggleWorking = (isoDate) => {
    setOverrides((prev) => {
      const next = { ...(prev || {}) };
      const cur = !!next[isoDate]?.working;
      next[isoDate] = { ...(next[isoDate] || {}), working: !cur };

      // keep it tidy: if "working" is false, remove override (since default is Off)
      if (!next[isoDate].working) delete next[isoDate];

      return next;
    });
  };

  const handleSave = async () => {
    try {
      await set("company_holiday_overrides", overrides);
      UIkit.notification("Saved holiday schedule.", "primary");
    } catch (e) {
      console.error("Holiday overrides save failed", e);
      UIkit.notification("Save failed", "danger");
    }
  };

  return (
    <li className={`setting-holidaySchedule-tab-content ${active === "holiday_schedule" ? "uk-active" : ""}`}>
      <div className="uk-card hms-left-tabs-content-card mx-w-665">
        <h2>Holiday Schedule</h2>

        <div className="uk-text-meta uk-margin-small-top">
          Federal holidays are off by default. Enable Working to open your company on a holiday. This applies to everyone unless overridden per user.
        </div>

        <div className="uk-margin uk-flex uk-flex-between uk-flex-middle" style={{ gap: 12 }}>
          <div>
            <label className="uk-form-label uk-text-meta">Year</label>
            <select
              className="uk-select"
              style={{ maxWidth: 160 }}
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="2026">2026</option>
              <option value="2027">2027</option>
              <option value="2028">2028</option>
            </select>
          </div>

          <div className="uk-text-meta uk-text-right">
            
          </div>
        </div>

        {/* Header row like your "Visible" label */}
        <div className="uk-width-1-1">
          <div className="uk-width-1-4 guide-embed-label">
            <div className="uk-flex uk-flex-right uk-flex-middle">
              <div>Working</div>
            </div>
          </div>
        </div>

        <ul className="uk-list">
          {holidaysForYear.map((h, idx) => (
            <li key={h.date} className="holiday-row">
              {/* Left side */}
              <div className="holiday-left">
                <div className="holiday-date">
                  {formatHolidayDate(h.date)}
                </div>
                <div className="holiday-name">
                  {h.label}
                </div>
              </div>

              {/* Right side */}
              <div className="holiday-right">
                <div className="autosend-checkbox">
                  <label htmlFor={`holidayWorking${idx}`} className="setting-switch">
                    <input
                      type="checkbox"
                      id={`holidayWorking${idx}`}
                      checked={isWorking(h.date)}
                      onChange={() => toggleWorking(h.date)}
                    />
                    <span className="setting-slider setting-round"></span>
                  </label>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export default HolidaySchedule;